import type { Device } from '@/entities/device';
import type { Asset } from '@/siteview/entities/asset';
import { AssetType } from '@/siteview/entities/asset-type';
import type { Filters } from '@/system-logs/view/state/selected-filters';

type BuildOptions = {
  siteId?: string;
  // When provided, include a from date set to now - dateRangeDays
  // If omitted, no date range is added to the URL.
  dateRangeDays?: number;
};

export function buildSystemLogsFilters(
  asset: Asset,
  device: Device | undefined | null,
): Filters {
  const isPcs = asset.type === AssetType.PCS;

  // Build domain-specific id filters from device lineage
  const levelKey = device?.level + 'Id';
  const batteryFilters: Filters['batteryFilters'] = isPcs
    ? {}
    : {
        ...{ [levelKey]: [asset.id] },
      };

  const pcsFilters: Filters['pcsFilters'] = isPcs
    ? {
        ...{ [levelKey]: [asset.id] },
      }
    : {};

  return {
    coreFilters: {
      dateRange: {},
      domain: isPcs ? 'pcs' : 'battery',
      siteId: asset.id,
    },
    batteryFilters,
    pcsFilters,
  };
}

export function buildSystemLogsSearch(
  asset: Asset,
  device: Device | undefined | null,
  opts: BuildOptions = {},
): string {
  const { dateRangeDays } = opts;
  const filters = buildSystemLogsFilters(asset, device);

  const params = new URLSearchParams();

  if (filters.coreFilters.domain) {
    params.set('domain', filters.coreFilters.domain);
  }

  if (dateRangeDays && Number.isFinite(dateRangeDays) && dateRangeDays > 0) {
    const from = new Date(Date.now() - dateRangeDays * 24 * 60 * 60 * 1000);
    params.set('from', from.toISOString());
  }

  // Encode level filters as arrays matching URL codec (comma-separated)
  if (filters.coreFilters.domain === 'battery') {
    const ids = filters.batteryFilters as any;
    if (ids.arrayId) params.set('array', (ids.arrayId as string[]).join(','));
    if (ids.nodeId) params.set('node', (ids.nodeId as string[]).join(','));
    if (ids.containerId)
      params.set('container', (ids.containerId as string[]).join(','));
    if (ids.bankId) params.set('bank', (ids.bankId as string[]).join(','));
    if (ids.packId) params.set('pack', (ids.packId as string[]).join(','));
    if (ids.moduleId)
      params.set('module', (ids.moduleId as string[]).join(','));
  } else if (filters.coreFilters.domain === 'pcs') {
    const ids = filters.pcsFilters as any;
    if (ids.systemId)
      params.set('system', (ids.systemId as string[]).join(','));
    if (ids.pcsId) params.set('pcs', (ids.pcsId as string[]).join(','));
  }

  return params.toString();
}
